import React, { Component } from 'react'
import { AiOutlineSearch, AiOutlineWarning, AiOutlineDown, AiOutlineRight, AiOutlineCheckCircle } from "react-icons/ai"
import { getInformationWithDataV3 } from '../../services/CABE'
import { getValueCookie } from '../../services/cookieService'
import { NumberFormat } from '../../functions/generalFunctions'
import { pricesFormat } from '../../functions/pricesFormat.js'
import "../../css/general-style.css"
import '../../css/dashboard.css'
import { Menu } from "../../components/Menu/Menu"

const LOW_MARGIN_THRESHOLD = 20

// Inline button styles — this page's own .actionBtn/.btnPrimaryCustom lookalikes
// turned out to live in OTHER pages' CSS Modules (AddNewProduct/ShowCustomerV2),
// not globally available here, so buttons rendered unstyled. Using the design
// token colors directly avoids depending on another page's scoped CSS.
const BTN_PRIMARY = { background: '#2563EB', border: 'none', borderRadius: '8px', color: '#FFFFFF', fontWeight: 700, padding: '0.5rem 1.1rem', fontSize: '0.85rem', cursor: 'pointer' }
const BTN_SECONDARY = { background: '#FFFFFF', border: '1px solid #E2E8F0', borderRadius: '8px', color: '#0F172A', fontWeight: 600, padding: '0.4rem 0.9rem', fontSize: '0.8rem', cursor: 'pointer' }

/**
 * Price Management — admin UI for the standardized pricing project (2026-08).
 *
 * Flow: browse the product catalog (union of itemCatalogue + itemCatalogueAltitude
 * — the same tables Sales Order's catalogue reads, confirmed 2026-08-13), click a product to
 * expand it, and fill in one row per company underneath — always inputs
 * (pre-filled if a product_pricing row already exists, empty if not), each with
 * its own Save that upserts just that company. No separate create/edit modal —
 * expanding a product IS the management UI.
 *
 * Completeness across companies is shown TWICE, same visual language both times
 * (icon + color), per the 2026-08-13 feedback that a faint row tint alone isn't
 * enough to scan a long list:
 *   - on the collapsed product row itself (icon + label, so nobody has to open
 *     every product just to check)
 *   - on each expanded company sub-row (already existed)
 * States: none (red) / partial (amber) / full (green-ish neutral).
 *
 * Only companies with company.status = 1 get a sub-row — status (not
 * systemCompanyStatus) is the real active flag, confirmed with the user
 * 2026-08-13 after a deactivated company still showed up.
 *
 * Only TypeUser === '1' can edit; everyone else sees the catalog + prices
 * read-only (expand still works, inputs are disabled). Every save is written
 * server-side to product_pricing_history for audit.
 *
 * Tiered customer pricing (2026-08-19): the old single "Standard" price per
 * company is now 3 tiers (Level 1/2/3) — customers get assigned a tier on the
 * new Customer Price Levels page, and Sales Order V3 prices the cart from
 * whichever tier the selected customer has (default Level 1). The old
 * standardPrice column is still saved unchanged in the background (not shown
 * here) so getMap/getCatalog can keep falling back to it for any item whose
 * levels aren't filled in yet — see productPricing.ts's upsert/getMap and
 * migrations/015_product_pricing_levels.sql.
 */
export default class ProductPricing extends Component {
    state = {
        companies: [],
        products: [],
        // pricing keyed by `${itemCode}__${idCompany}` — one entry per existing
        // product_pricing row, from the same /v3/pricing/catalog response as
        // `products` (single round-trip for the whole page of products).
        pricing: {},
        // Local input state per company sub-row, same key shape as `pricing`,
        // seeded from `pricing` on expand and updated as the admin types —
        // kept separate from `pricing` so typing doesn't require a re-fetch.
        edits: {},
        expanded: {}, // itemCode -> true when expanded
        saving: {}, // key -> true while that row's save is in flight
        search: '',
        page: 1,
        pageSize: 50,
        total: 0,
        loading: false,
        loaded: false,
        errorMessage: '',
    }

    isAdmin = getValueCookie('TypeUser') === '1'

    componentDidMount() {
        this.fetchCompanies()
        this.fetchCatalog()
    }

    async fetchCompanies() {
        try {
            const resp = await getInformationWithDataV3('/v3/pricing/companies', {})
            if (resp && resp.status && resp.status.code === 1 && Array.isArray(resp.data)) {
                this.setState({ companies: resp.data })
            }
        } catch (error) {
            console.error('fetchCompanies failed:', error)
        }
    }

    pricingKey(itemCode, idCompany) {
        return itemCode + '__' + idCompany
    }

    async fetchCatalog() {
        this.setState({ loading: true, errorMessage: '' })
        try {
            const resp = await getInformationWithDataV3('/v3/pricing/catalog', {
                search: this.state.search || undefined,
                page: this.state.page,
                pageSize: this.state.pageSize,
            })
            if (!resp || !resp.status || resp.status.code !== 1) {
                this.setState({ loading: false, loaded: true, errorMessage: 'Could not load the product catalog.' })
                return
            }
            const products = (resp.data && Array.isArray(resp.data.products)) ? resp.data.products : []
            const pricingRows = (resp.data && Array.isArray(resp.data.pricing)) ? resp.data.pricing : []
            const pricing = {}
            for (const row of pricingRows) {
                pricing[this.pricingKey(row.itemCode, row.idCompany)] = row
            }
            this.setState({ loading: false, loaded: true, products, pricing, total: resp.total || products.length })
        } catch (error) {
            this.setState({ loading: false, loaded: true, errorMessage: error.message || 'Error loading the catalog.' })
        }
    }

    toggleExpand(product) {
        const itemCode = product.itemCode
        const isExpanding = !this.state.expanded[itemCode]
        this.setState((prev) => ({ expanded: { ...prev.expanded, [itemCode]: isExpanding } }))

        if (!isExpanding) return
        // Seed local edit inputs for every ACTIVE company from whatever pricing
        // already exists (or empty, for a company with no row yet) — this is
        // what makes each sub-row a ready-to-save upsert form the moment it's visible.
        this.setState((prev) => {
            const edits = { ...prev.edits }
            for (const company of prev.companies) {
                const key = this.pricingKey(itemCode, company.idCompany)
                if (edits[key]) continue
                const existing = prev.pricing[key]
                // Cost pre-fills from the existing row if there is one, otherwise
                // from the synced catalog value as a starting suggestion — either
                // way it's editable, since not every item has a synced cost yet.
                const suggestedCost = (existing && existing.cost !== null && existing.cost !== undefined)
                    ? existing.cost
                    : (product.standardUnitCost !== null && product.standardUnitCost !== undefined ? product.standardUnitCost : '')
                edits[key] = {
                    cost: suggestedCost,
                    // standardPrice is kept (not shown in the form anymore — see
                    // header comment) purely so saveCompanyRow can send it back
                    // unchanged; upsert() nulls out any field it doesn't receive,
                    // and getMap/getCatalog still fall back to this column for
                    // items whose level prices aren't filled in yet.
                    standardPrice: (existing && existing.standardPrice !== null && existing.standardPrice !== undefined) ? existing.standardPrice : '',
                    minPrice: (existing && existing.minPrice !== null && existing.minPrice !== undefined) ? existing.minPrice : '',
                    maxPrice: (existing && existing.maxPrice !== null && existing.maxPrice !== undefined) ? existing.maxPrice : '',
                    level1Price: (existing && existing.level1Price !== null && existing.level1Price !== undefined) ? existing.level1Price : '',
                    level2Price: (existing && existing.level2Price !== null && existing.level2Price !== undefined) ? existing.level2Price : '',
                    level3Price: (existing && existing.level3Price !== null && existing.level3Price !== undefined) ? existing.level3Price : '',
                }
            }
            return { edits }
        })
    }

    setEditValue(itemCode, idCompany, field, value) {
        const key = this.pricingKey(itemCode, idCompany)
        this.setState((prev) => ({
            edits: {
                ...prev.edits,
                [key]: { ...prev.edits[key], [field]: value },
            },
        }))
    }

    async saveCompanyRow(product, idCompany) {
        const key = this.pricingKey(product.itemCode, idCompany)
        const edit = this.state.edits[key] || { cost: '', standardPrice: '', minPrice: '', maxPrice: '', level1Price: '', level2Price: '', level3Price: '' }
        this.setState((prev) => ({ saving: { ...prev.saving, [key]: true } }))
        try {
            const resp = await getInformationWithDataV3('/v3/pricing/upsert', {
                itemCode: product.itemCode,
                idCompany,
                cost: edit.cost,
                standardPrice: edit.standardPrice,
                minPrice: edit.minPrice,
                maxPrice: edit.maxPrice,
                level1Price: edit.level1Price,
                level2Price: edit.level2Price,
                level3Price: edit.level3Price,
            })
            if (!resp || !resp.status || resp.status.code !== 1) {
                this.setState((prev) => ({ saving: { ...prev.saving, [key]: false }, errorMessage: 'Could not save ' + product.itemCode + '.' }))
                return
            }
            // Patch just this row into `pricing` — avoids re-fetching the whole
            // page just to reflect one save, and keeps completeness live.
            this.setState((prev) => ({
                saving: { ...prev.saving, [key]: false },
                pricing: { ...prev.pricing, [key]: resp.data },
            }))
        } catch (error) {
            this.setState((prev) => ({ saving: { ...prev.saving, [key]: false }, errorMessage: error.message || 'Error saving.' }))
        }
    }

    // Completeness across companies — drives both the product-row indicator and
    // the sub-row background. This IS the "how do I get started" answer: nothing
    // needs a separate seed step, expand any product and fill in what's missing.
    completeness(itemCode) {
        const { companies, pricing } = this.state
        if (companies.length === 0) return { state: 'unknown', configured: 0, missing: 0, total: 0 }
        let configured = 0
        for (const company of companies) {
            if (pricing[this.pricingKey(itemCode, company.idCompany)]) configured++
        }
        const total = companies.length
        const missing = total - configured
        const state = configured === 0 ? 'none' : configured < total ? 'partial' : 'full'
        return { state, configured, missing, total }
    }

    rowBackground(completenessState) {
        if (completenessState === 'none') return 'rgba(239,68,68,0.06)'
        if (completenessState === 'partial') return 'rgba(245,158,11,0.08)'
        return '#F8FAFC'
    }

    // Icon + label badge — same visual language used on the sub-rows, now also
    // on the product row itself so completeness is visible without expanding.
    completenessBadge(completenessInfo) {
        const { state, missing } = completenessInfo
        if (state === 'none') {
            return <span style={{ display: 'inline-flex', alignItems: 'center', gap: '0.35rem', color: '#991B1B', fontWeight: 700, fontSize: '0.78rem' }}>
                <AiOutlineWarning /> Not configured
            </span>
        }
        if (state === 'partial') {
            return <span style={{ display: 'inline-flex', alignItems: 'center', gap: '0.35rem', color: '#92400E', fontWeight: 700, fontSize: '0.78rem' }}>
                <AiOutlineWarning /> Missing {missing}
            </span>
        }
        return <span style={{ display: 'inline-flex', alignItems: 'center', gap: '0.35rem', color: '#065F46', fontWeight: 700, fontSize: '0.78rem' }}>
            <AiOutlineCheckCircle /> Configured
        </span>
    }

    marginPreview(standardPrice, cost) {
        const sp = Number(standardPrice)
        const c = Number(cost)
        if (!sp || sp <= 0 || !isFinite(c)) return null
        return Math.round(((sp - c) / sp) * 10000) / 100
    }

    marginStyle(margin) {
        if (margin === null || margin === undefined) return { color: '#64748B', background: 'transparent', border: '1px solid #E2E8F0' }
        if (margin < LOW_MARGIN_THRESHOLD) return { color: '#991B1B', background: 'rgba(239,68,68,0.08)', border: '1px solid #FECACA' }
        return { color: '#065F46', background: 'rgba(16,185,129,0.08)', border: '1px solid #A7F3D0' }
    }

    render() {
        const { products, companies, pricing, edits, expanded, saving, loading, errorMessage, search, page, pageSize, total } = this.state
        const totalPages = Math.max(1, Math.ceil(total / pageSize))

        return (
            <div className="dash-page" style={{ paddingTop: '30px' }}>
                <Menu />

                <div style={{ background: 'linear-gradient(135deg, #0f172a 0%, #1e3a5f 60%, #2563EB 100%)', padding: '2rem 2rem' }}>
                    <div style={{ flex: 1 }}>
                        <h1 style={{ color: 'white', fontWeight: 800, fontSize: '1.5rem', margin: 0 }}>Price Management</h1>
                        <p style={{ color: 'rgba(255,255,255,0.65)', margin: '0.25rem 0 0', fontSize: '0.85rem' }}>
                            Click a product to configure its price per company
                        </p>
                    </div>
                </div>

                <div style={{ padding: '1.5rem 2rem' }}>
                    <div className="card mb-3">
                        <div className="card-body" style={{ display: 'flex', gap: '1.5rem', flexWrap: 'wrap', alignItems: 'center' }}>
                            <div style={{ flex: 1, minWidth: '220px' }}>
                                <div style={{ position: 'relative' }}>
                                    <AiOutlineSearch style={{ position: 'absolute', left: '10px', top: '10px', color: '#94A3B8' }} />
                                    <input type="text" className="form-control" style={{ paddingLeft: '32px' }}
                                        placeholder="Search item code or description..." value={search}
                                        onChange={(e) => this.setState({ search: e.target.value })}
                                        onKeyDown={(e) => { if (e.key === 'Enter') this.setState({ page: 1 }, () => this.fetchCatalog()) }} />
                                </div>
                            </div>
                            <button style={BTN_PRIMARY} onClick={() => this.setState({ page: 1 }, () => this.fetchCatalog())}>Search</button>
                        </div>
                    </div>

                    {errorMessage && (
                        <div className="metric-card status-danger mb-3">
                            <div style={{ color: '#DC2626', fontSize: '0.85rem' }}>{errorMessage}</div>
                        </div>
                    )}

                    <div className="card">
                        <div className="tableFixHeadSmall">
                            <table className="table table-clean">
                                <thead>
                                    <tr>
                                        <th></th>
                                        <th>Item Code</th>
                                        <th>Description</th>
                                        <th>Line</th>
                                        <th>Status</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {products.length === 0 && !loading && (
                                        <tr><td colSpan={5} className="empty">No results</td></tr>
                                    )}
                                    {products.map((product) => {
                                        const isOpen = !!expanded[product.itemCode]
                                        const completenessInfo = this.completeness(product.itemCode)
                                        return (
                                            <React.Fragment key={product.itemCode}>
                                                <tr onClick={() => this.toggleExpand(product)} style={{ background: this.rowBackground(completenessInfo.state), cursor: 'pointer' }}>
                                                    <td style={{ width: '32px', textAlign: 'center' }}>{isOpen ? <AiOutlineDown /> : <AiOutlineRight />}</td>
                                                    <td className="mono">{product.itemCode}</td>
                                                    <td className="small">{product.description || '—'}</td>
                                                    <td className="small">{product.productLine || '—'}</td>
                                                    <td>{this.completenessBadge(completenessInfo)}</td>
                                                </tr>
                                                {isOpen && companies.map((company) => {
                                                    const key = this.pricingKey(product.itemCode, company.idCompany)
                                                    const existing = pricing[key]
                                                    const edit = edits[key] || { cost: '', standardPrice: '', minPrice: '', maxPrice: '', level1Price: '', level2Price: '', level3Price: '' }
                                                    // Live preview, recomputed from whatever's currently typed. Margin is
                                                    // against Level 1 — that's the new default price (2026-08-19, tiered
                                                    // pricing), replacing the old Standard column for this purpose.
                                                    const previewMargin = this.marginPreview(edit.level1Price, edit.cost)
                                                    const mStyle = this.marginStyle(previewMargin)
                                                    const isSaving = !!saving[key]
                                                    return (
                                                        <tr key={key} style={{ background: '#FFFFFF' }}>
                                                            <td></td>
                                                            <td colSpan={4}>
                                                                <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', flexWrap: 'wrap', padding: '6px 0 6px 24px', borderLeft: '3px solid ' + (existing ? '#A7F3D0' : '#FECACA') }}>
                                                                    <div style={{ minWidth: '160px', display: 'flex', alignItems: 'center', gap: '0.4rem', fontWeight: 700, fontSize: '0.85rem' }}>
                                                                        {existing ? <AiOutlineCheckCircle style={{ color: '#059669' }} /> : <AiOutlineWarning style={{ color: '#DC2626' }} />}
                                                                        {company.name}
                                                                    </div>
                                                                    <div>
                                                                        <div style={{ fontSize: '0.68rem', color: '#94A3B8' }}>Cost</div>
                                                                        <input type="number" step="0.01" disabled={!this.isAdmin} className="form-control form-control-sm" style={{ width: '90px' }}
                                                                            value={edit.cost}
                                                                            onChange={(e) => this.setEditValue(product.itemCode, company.idCompany, 'cost', e.target.value)} />
                                                                    </div>
                                                                    <div>
                                                                        <div style={{ fontSize: '0.68rem', color: '#94A3B8' }}>Level 1 <span style={{ color: '#94A3B8', fontWeight: 400 }}>(default)</span></div>
                                                                        <input type="number" step="0.01" disabled={!this.isAdmin} className="form-control form-control-sm" style={{ width: '100px' }}
                                                                            value={edit.level1Price}
                                                                            onChange={(e) => this.setEditValue(product.itemCode, company.idCompany, 'level1Price', e.target.value)} />
                                                                    </div>
                                                                    <div>
                                                                        <div style={{ fontSize: '0.68rem', color: '#94A3B8' }}>Level 2</div>
                                                                        <input type="number" step="0.01" disabled={!this.isAdmin} className="form-control form-control-sm" style={{ width: '100px' }}
                                                                            value={edit.level2Price}
                                                                            onChange={(e) => this.setEditValue(product.itemCode, company.idCompany, 'level2Price', e.target.value)} />
                                                                    </div>
                                                                    <div>
                                                                        <div style={{ fontSize: '0.68rem', color: '#94A3B8' }}>Level 3</div>
                                                                        <input type="number" step="0.01" disabled={!this.isAdmin} className="form-control form-control-sm" style={{ width: '100px' }}
                                                                            value={edit.level3Price}
                                                                            onChange={(e) => this.setEditValue(product.itemCode, company.idCompany, 'level3Price', e.target.value)} />
                                                                    </div>
                                                                    <div>
                                                                        <div style={{ fontSize: '0.68rem', color: '#94A3B8' }}>Min</div>
                                                                        <input type="number" step="0.01" disabled={!this.isAdmin} className="form-control form-control-sm" style={{ width: '90px' }}
                                                                            value={edit.minPrice}
                                                                            onChange={(e) => this.setEditValue(product.itemCode, company.idCompany, 'minPrice', e.target.value)} />
                                                                    </div>
                                                                    <div>
                                                                        <div style={{ fontSize: '0.68rem', color: '#94A3B8' }}>Max</div>
                                                                        <input type="number" step="0.01" disabled={!this.isAdmin} className="form-control form-control-sm" style={{ width: '90px' }}
                                                                            value={edit.maxPrice}
                                                                            onChange={(e) => this.setEditValue(product.itemCode, company.idCompany, 'maxPrice', e.target.value)} />
                                                                    </div>
                                                                    <div>
                                                                        <div style={{ fontSize: '0.68rem', color: '#94A3B8' }}>Margin</div>
                                                                        <span style={{ ...mStyle, borderRadius: '6px', padding: '4px 8px', fontSize: '0.75rem', fontWeight: 700, display: 'inline-block' }}>
                                                                            {previewMargin === null ? '—' : `${previewMargin}%`}
                                                                        </span>
                                                                    </div>
                                                                    {this.isAdmin && (
                                                                        <button style={{ ...BTN_PRIMARY, padding: '6px 14px', fontSize: '0.78rem' }}
                                                                            disabled={isSaving} onClick={() => this.saveCompanyRow(product, company.idCompany)}>
                                                                            {isSaving ? 'Saving...' : 'Save'}
                                                                        </button>
                                                                    )}
                                                                </div>
                                                            </td>
                                                        </tr>
                                                    )
                                                })}
                                            </React.Fragment>
                                        )
                                    })}
                                </tbody>
                            </table>
                        </div>
                        <div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: '0.75rem', padding: '12px 16px' }}>
                            <span style={{ fontSize: '0.8rem', color: '#64748B' }}>Page {page} of {totalPages} ({total} products)</span>
                            <button style={{ ...BTN_SECONDARY, opacity: page <= 1 ? 0.5 : 1 }}
                                disabled={page <= 1} onClick={() => this.setState({ page: page - 1 }, () => this.fetchCatalog())}>Previous</button>
                            <button style={{ ...BTN_SECONDARY, opacity: page >= totalPages ? 0.5 : 1 }}
                                disabled={page >= totalPages} onClick={() => this.setState({ page: page + 1 }, () => this.fetchCatalog())}>Next</button>
                        </div>
                    </div>
                </div>
            </div>
        )
    }
}
